import React, { FC, MouseEvent, useCallback } from 'react'
import styled from 'styled-components'
import { IcArrowRight } from '@healingpaper/cell-icons'
import Link from 'next/link'

import Text from '@/components/atomic/atoms/Text'
import Icon from '@/components/cell/atoms/Icon'
import { IconPack } from '@/components/cell/atoms/Icon/enum'
import COLOR from '@/components/cell/designTokens/color'

export interface BreadcrumbDepth {
  text: string
  href?: string
  onClick?: (e: MouseEvent) => void
}

interface Props {
  depth: BreadcrumbDepth[]
  movePage?: (href: string) => void
  className?: string
  'data-testid'?: string
}
const BreadCrumb: FC<Props> = ({ depth, movePage, className, 'data-testid': dataTestId }) => {
  const move = useCallback((e: MouseEvent, item: BreadcrumbDepth) => {
    e.preventDefault()
    if (item.onClick) {
      item.onClick(e)
    }
    if (movePage && item.href) {
      movePage(item.href)
    }
  }, [])

  const BASE_INDEX = 1

  return (
    <Nav className={className} data-testid={dataTestId}>
      <ol className="flex-wrap">
        <li data-testid={'breadcrumb_li_0'}>
          <StyledSymbol href={depth[0].href || ''} onClick={depth[0]?.onClick} aria-label="Navigation Link">
            <Icon name={IconPack.GNUN_SYMBOL} width={15} height={15} />
          </StyledSymbol>
        </li>
        {depth.slice(1).map((item, index) => (
          <li key={`breadcrumb_arrow_${index}`} data-testid={`breadcrumb_li_${index + BASE_INDEX}`}>
            <IcArrowRight color={COLOR.gray500} width={18} height={18} />
            <Text element="span" type="caption2">
              {item.href ? (
                <Link href={item.href} onClick={(e) => move(e, item)} aria-label="Navigation Link">
                  {item.text}
                </Link>
              ) : (
                <span>{item.text}</span>
              )}
            </Text>
          </li>
        ))}
      </ol>
    </Nav>
  )
}

const StyledSymbol = styled(Link)`
  display: flex;
  align-items: center;
  margin-bottom: 1px;
`

const Nav = styled.nav`
  display: flex;
  align-items: center;
  height: auto;
  padding-top: 16px;
  padding-bottom: 16px;

  ol {
    width: 100%;
    display: flex;
    list-style: none;
    margin: 0px;
    padding: 0px;
    white-space: nowrap;
    li {
      display: flex;
      align-items: center;

      & ~ li svg {
        margin-left: 2px;
        margin-right: 2px;
      }

      span {
        overflow: hidden;
        text-overflow: ellipsis;
      }

      a {
        text-decoration: none;
        color: ${COLOR.black} !important;
        &:hover {
          text-decoration: underline;
        }
      }
      &:last-child {
        overflow: hidden;
        margin-right: 16px;
      }
      &:first-child {
        svg {
          margin-top: 3px;
        }
      }
    }
  }
`

export default BreadCrumb
